import { Text } from "@chakra-ui/react";
import { Box, Flex } from "@chakra-ui/layout";
import { Tag } from "@chakra-ui/tag";
import { RespostaMensagemForm } from "core/features/mensagens/typings";
import colors from "core/resources/theme/colors";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { FaShare } from "react-icons/fa";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import Field from "ui/components/Input/Field";
import useResponderMensagem from "core/features/mensagens/hooks/useResponderMensagem";
import { showErrorAxios } from "infra/helpers/alerta";
import MessageEditor from "ui/components/MessageEditor";
import ButtonAnexarArquivos from "ui/components/Button/ButtonAnexarArquivos";
import Anexos from "ui/components/Anexos";

interface ResponderMensagemProps {
  mensagemRespostaId: string;
  nomeDestino: string;
  onClose: () => any;
  onSuccess?: () => any;
}

function ResponderMensagem({
  mensagemRespostaId,
  nomeDestino,
  onClose,
  onSuccess,
}: ResponderMensagemProps) {
  const [clickCount, setClickCount] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const { control, handleSubmit, watch, setValue } =
    useForm<RespostaMensagemForm>({
      mode: "all",
    });

  const { mutate: responderMensagem, isLoading: isLoadingResponder } =
    useResponderMensagem(mensagemRespostaId);

  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      const containerEl = document.getElementById(
        `container-resposta-${mensagemRespostaId}`
      );

      if (
        containerEl !== ev.target &&
        !containerEl?.contains(ev.target as any)
      ) {
        setClickCount((count) => count + 1);
      } else {
        setClickCount(0);
      }
    };
    window.addEventListener("click", handler);

    return () => window.removeEventListener("click", handler);
  }, [mensagemRespostaId]);

  useEffect(() => {
    if (clickCount >= 3) {
      onClose();
    }
  }, [clickCount, onClose]);

  const form = watch();
  const onSubmit = (data: RespostaMensagemForm) => {
    responderMensagem(data, {
      onSuccess,
      onError: (e) => {
        showErrorAxios(e);
      },
    });
  };

  const onChange = useCallback(
    (html: string | undefined, plainText?: string) => {
      setValue("conteudo", html || "");
      setValue("conteudoPlain", plainText);
    },
    [setValue]
  );

  const onStartTyping = useCallback(() => {
    setIsTyping(true);
  }, []);
  const onEndTyping = useCallback(() => {
    setIsTyping(false);
  }, []);

  return (
    <Flex
      flexDir="column"
      py={6}
      id={`container-resposta-${mensagemRespostaId}`}
    >
      <Flex gridGap={4} mb={4}>
        <Box as="span" transform="scaleX(-1)">
          <FaShare color={colors.black700} size={16} />
        </Box>

        <Tag colorScheme="gray" variant="outline">
          {nomeDestino}
        </Tag>
      </Flex>

      <Field
        name="conteudo"
        control={control}
        render={({ field }) => {
          field.ref(inputRef.current);
          return (
            <MessageEditor
              id={mensagemRespostaId}
              value={field.value}
              onChange={onChange}
              onStartTyping={onStartTyping}
              onEndTyping={onEndTyping}
              heightType={2}
              autoFocus
            />
          );
        }}
      />

      {form.anexos?.length ? (
        <Box my={4}>
          <Anexos
            setValue={(anexos) => setValue("anexos", anexos as File[])}
            anexos={form.anexos}
          />
        </Box>
      ) : null}

      <Flex w="100%" gridGap={4} alignItems="center">
        {clickCount === 2 ? (
          <Text mr="auto" color={colors.red} fontWeight="500" fontSize={16}>
            Clique fora novamente para fechar
          </Text>
        ) : null}
        {/* <OutlineButton ml="auto">Anexar arquivos</OutlineButton> */}

        <Flex ml="auto" alignItems="center" mt={4}>
          <ButtonAnexarArquivos
            variant="outline"
            onAnexar={(files) => {
              if (files) {
                const currentAnexos = form.anexos;

                setValue(
                  "anexos",
                  currentAnexos ? [...currentAnexos, ...files] : files
                );
              }
            }}
          />

          <PrimaryButton
            ml="16px"
            isLoading={isLoadingResponder}
            onClick={handleSubmit(onSubmit)}
            disabled={!form.conteudo || isTyping}
          >
            Enviar mensagem
          </PrimaryButton>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default ResponderMensagem;
